import type { FolderAdded } from '@deepcloud/api-deep-box'
import type { FolderWithMeta } from './use-folder-tree-builder'
import type { useTaskUploads } from './use-task-uploads'

export function useFolderTreeReconciler(
  taskUploads: ReturnType<typeof useTaskUploads>,
) {
  const { getTaskUploadById, setTaskUploadFolderNode } = taskUploads

  /**
   * Mark every folder in the (meta) tree as processing before the API call.
   */
  function markFolderTreeProcessing(
    taskUploadId: number,
    apiFolders: FolderWithMeta[],
  ) {
    for (const metaFolder of apiFolders) {
      setTaskUploadFolderNode(taskUploadId, metaFolder._folderId, {
        status: 'processing',
        progress: 0,
      })
      if (metaFolder.children?.length) {
        markFolderTreeProcessing(taskUploadId, metaFolder.children)
      }
    }
  }

  /**
   * Mark every folder in the (meta) tree as errored after a failed API call.
   */
  async function markFolderTreeError(
    taskUploadId: number,
    apiFolders: FolderWithMeta[],
    error: Error,
  ) {
    for (const metaFolder of apiFolders) {
      setTaskUploadFolderNode(taskUploadId, metaFolder._folderId, {
        status: 'error',
        data: {
          ...getTaskUploadById(taskUploadId)?.folders?.[metaFolder._folderId]
            ?.data,
          error,
        },
      })

      const folderNode =
        getTaskUploadById(taskUploadId)?.folders?.[metaFolder._folderId]
      if (folderNode?.onFailure) {
        await folderNode.onFailure(taskUploadId, metaFolder._folderId)
      }

      if (metaFolder.children?.length) {
        await markFolderTreeError(taskUploadId, metaFolder.children, error)
      }
    }
  }
  /**
   * After a successful createFolderTree call the API returns a single root Node
   * that mirrors the shape of the Folder[] we sent.  Walk both trees in lockstep
   * and update every TaskUploadNode with its new nodeId / Node.
   */
  async function reconcileFolderTree(
    taskUploadId: number,
    apiFolders: FolderWithMeta[],
    responseItems: FolderAdded[],
  ): Promise<void> {
    const callbacks: Array<() => Promise<void>> = []

    function collectUpdates(
      metaFolders: FolderWithMeta[],
      items: FolderAdded[],
    ) {
      for (let i = 0; i < metaFolders.length; i++) {
        const metaFolder = metaFolders[i]
        const responseItem = items?.[i]

        if (!metaFolder) continue

        if (responseItem?.node) {
          setTaskUploadFolderNode(taskUploadId, metaFolder._folderId, {
            status: 'completed',
            progress: 100,
            data: {
              ...getTaskUploadById(taskUploadId)?.folders?.[
                metaFolder._folderId
              ]?.data,
              nodeId: responseItem.node.nodeId,
              node: responseItem.node,
            },
          })

          const folderNode =
            getTaskUploadById(taskUploadId)?.folders?.[metaFolder._folderId]
          if (folderNode?.onSuccess) {
            const cb = folderNode.onSuccess
            callbacks.push(() => cb(taskUploadId, metaFolder._folderId))
          }

          if (metaFolder.children?.length && responseItem.children?.length) {
            collectUpdates(metaFolder.children, responseItem.children)
          }
        } else {
          setTaskUploadFolderNode(taskUploadId, metaFolder._folderId, {
            status: 'error',
            data: {
              ...getTaskUploadById(taskUploadId)?.folders?.[
                metaFolder._folderId
              ]?.data,
              error: new Error('FOLDER_NODE_MISSING_IN_RESPONSE'),
            },
          })

          const folderNode =
            getTaskUploadById(taskUploadId)?.folders?.[metaFolder._folderId]
          if (folderNode?.onFailure) {
            const cb = folderNode.onFailure
            callbacks.push(() => cb(taskUploadId, metaFolder._folderId))
          }
        }
      }
    }

    //  update all folder states in one pass
    collectUpdates(apiFolders, responseItems)

    // fire all callbacks in parallel
    await Promise.all(callbacks.map((cb) => cb()))
  }

  return { markFolderTreeProcessing, markFolderTreeError, reconcileFolderTree }
}
